import { ApolloError, useQuery } from '@apollo/client';
import { useObserver } from 'mobx-react-lite';

import useStores from 'shared/hooks/use-stores';
import { useGetDegradedPollInterval } from 'shared/hooks/use-get-degraded-poll-interval';
import { GetOrderForConfirmationDocument, GetOrderForConfirmationQuery } from 'src/gql/graphql';

type UseOrderForConfirmationParams = {
  orderId: string;
};

export type ConfirmationOrder = NonNullable<
  NonNullable<NonNullable<GetOrderForConfirmationQuery['filteredOrders']>['orders']>[number]
>;

type UseOrderForConfirmationReturn = {
  order: ConfirmationOrder | undefined;
  loading: boolean;
  error: ApolloError | null;
};

const DEFAULT_POLL_INTERVAL_MS = 60_000;
const DEGRADATION_MULTIPLIER = 5;

// Real-time order updates arrive via the parent OrderStatusModal's
// useOrderForConsumerView Pusher subscription, which writes to the same
// normalized Order entity in Apollo's cache. cache-and-network surfaces those
// updates here automatically. Polling remains as a degraded-network safety net.
export function useOrderForConfirmation({ orderId }: UseOrderForConfirmationParams): UseOrderForConfirmationReturn {
  const { User } = useStores();
  const userId: string | undefined = useObserver(() => User.id);
  const pollInterval = useGetDegradedPollInterval({
    milliseconds: DEFAULT_POLL_INTERVAL_MS,
    degradationMultiplier: DEGRADATION_MULTIPLIER,
  });

  const skip = !userId || !orderId;

  const { data, loading, error } = useQuery(GetOrderForConfirmationDocument, {
    variables: { ordersFilter: { orderId, userId } },
    fetchPolicy: 'cache-and-network',
    pollInterval,
    skip,
  });

  const order = skip ? undefined : data?.filteredOrders?.orders?.[0] ?? undefined;

  return {
    order,
    loading,
    error: error ?? null,
  };
}
